import { z } from 'zod';

// Week start day enum (shared with Restaurant)
const WeekStartDaySchema = z.enum(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']);

// Base tenant settings schema
const TenantSettingsSchema = z.object({
  timezone: z.string().readonly(),
  currency: z.string().length(3).readonly(), // ISO 4217 currency codes
  dateFormat: z.string().readonly(),
  weekStartDay: WeekStartDaySchema.readonly(), // Which day the business week starts on
  features: z.array(z.string()).readonly(),
}).strict();

// Main Tenant schema - matches our model exactly
const TenantSchema = z.object({
  tenantId: z.number().positive().readonly(),
  guid: z.string().readonly(),
  name: z.string().min(1, { message: "Business name is required" }).readonly(),
  displayName: z.string().min(1, { message: "Display name is required" }).readonly(),
  status: z.enum(['active', 'inactive', 'suspended']).readonly(),
  plan: z.enum(['starter', 'professional', 'enterprise']).readonly(),
  settings: TenantSettingsSchema.readonly(),
  suspendedAt: z.date().optional().readonly(),
  purgeAfter: z.date().optional().readonly(),
  purgedAt: z.date().optional().readonly(),
  deletionReason: z.enum(['self_cancel', 'ops_offboard', 'nonpayment', 'fraud_abuse', 'ownership_transfer']).optional().readonly(),
  deletionActorUid: z.string().optional().readonly(),
  createdAt: z.date().readonly(),
  updatedAt: z.date().readonly(),
}).strict();

// Schema for creating a new tenant
const CreateTenantSchema = z.object({
  name: z.string().min(1, { message: "Business name is required" }),
  displayName: z.string().min(1, { message: "Display name is required" }),
  plan: z.enum(['starter', 'professional', 'enterprise']).optional(),
  settings: TenantSettingsSchema.partial().optional(),
});

// Schema for updating tenant status
const UpdateTenantStatusSchema = z.object({
  id: z.string().min(1, { message: "Tenant ID is required" }),
  status: z.enum(['active', 'inactive', 'suspended']),
});

// Schema for updating tenant details
const UpdateTenantSchema = TenantSchema.partial().omit({
  tenantId: true,
  guid: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().min(1, { message: "Tenant ID is required" }),
});

// Export types for TypeScript usage
export type TenantType = z.infer<typeof TenantSchema>;
export type CreateTenantType = z.infer<typeof CreateTenantSchema>;
export type UpdateTenantStatusType = z.infer<typeof UpdateTenantStatusSchema>;
export type UpdateTenantType = z.infer<typeof UpdateTenantSchema>;
export type TenantSettingsType = z.infer<typeof TenantSettingsSchema>;

export default TenantSchema;
export {
  TenantSettingsSchema,
  CreateTenantSchema,
  UpdateTenantStatusSchema,
  UpdateTenantSchema,
}; 